import { turnCommitFrame, applyTurnCommitClock, type TurnCommitFrame } from '../janus-engine/turns/turn-commit-frame.js';
import { appliedFrameOf, exposeAppliedFrame, stageAppliedEntities, takeAppliedEntities } from './applied-result';
import { memberAppliedFrame, appliedChargeRows, appliedSpawnRows } from '../janus-engine/transport/member-applied-frame';
import { counterspellWatchRows } from '../janus-engine/ability/held-counterspell-watches';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { hasSurfaceTypeValue, paoeEntryAlreadyResolved } from '../janus-engine/ability/paoe-entry-once.js';
import { readPaoeEntryRecords, readSurfaceEntryRecords } from './paoe-entry-records.js';
import { afterActionPaint } from '../shared/after-action-paint';
import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { ftCurrentTruthSink, trainingGroundsRefusalMessage } from '../shared/truth-sink';
import { catalogSnapshot, retainHeldBase } from '../janus-engine/ability/frame-snapshot';
import { withLogParticipants } from '../shared/log-participant-snapshot';
import { awaitEntityActionReady, entityCompileReady } from '../shared/entity-compile-ready';
import { startActionTiming, markActionTiming, bindActionTiming, endActionTiming } from '../shared/action-timing';
import { withAsyncDeadline } from '../shared/async-deadline';
// turn-truth.ts — T1 (End Turn): the client-truth THREE-ROW transport adapter + WS subscriber.
//
// Mirrors action-truth.ts / attack-truth.ts on the shared per-actor outbox: the pure resolver (janus-engine/turns/end-turn.ts)
// decides, the ENDING actor's row, the NEXT actor's row, any THIRD loser row and the encounter scalars are mutated in place
// and rendered at once; the browser pushes that truth while turn_truth_commit_inc.php saves in parallel.
//
// Stale saves retain the participant correction path; successful saves patch held rows.
import {
  applyEndTurnTruth, END_TURN_SCHEMA, resolveEndTurn, resolveTurnDeath,
  type AbilityEntry, type EndTurnCommand, type EndTurnFrame, type EndTurnReaders,
} from '../janus-engine/turns/end-turn.js';
import { type TurnTickResult } from '../janus-engine/turns/ticks.js';
import { campaignHouseRules } from '../janus-engine/ability/house-rules.js';
import { isMovementTruthAcceptedResult, MovementTruthRejected, MovementTruthTerminal, type ActorTruthCommandBase, type MovementTruthAcceptedResult, type MovementTruthRejectedResult, type MovementTruthRow, type MovementTruthTerminalInfo } from '../janus-engine/movement/client-truth-commit.js';
import { creatureIsInActiveCombat } from '../roll-init/client-combat-membership.js';
import { waterWetHeld } from '../janus-engine/movement/water-wet.js';
import { ftCampaignNowSeconds, ftCampaignTimeAddSeconds, ftClockCalendarFromArr, ftClockColsFromArr, type CampaignClockCols } from '../shared/campaign-clock.js';
import { encounterField, encounterCreatureField, type CasterArr } from '../shared/array-types.js';
import { _ftCamField } from '../shared/campaign-field.js';
import { handleCasterUpdate } from './caster-update.js';
import { registerTurnDeathReader } from './turn-death-registry.js';
import { refusalOf } from './truth-refusal-notice.js';
import { fetchAndCacheCaster } from './caster-cache.js';
import { actorEncounterFor, actorOutbox, actorRowFor, bankConfirmedRev, confirmedRevFor, noteFrameSeen, noteOwnOperation, pendingFor, reconcileActorRow, registerTruthSchema } from './movement-truth.js';
import { pickActionSound, type ActionSoundCue } from '../shared/audio-catalog.js';
import { playActionSound } from './roll-audio-mixer.js';
import { type PageTriggerConsent, askTriggerConsent, createTriggerConsent, offerFortunate } from './trigger-consent.js';
import { endOfTurnZonesFor } from './zone-truth.js';
import { encounterLightTypesPresent } from '../encounter-map/ft3d/light-presence';
import { placedReaders, hydrateTurnTriggerDefinitions, fetchObjectsBatch } from './ability-truth.js';
import { createLegendaryWindowActions, legendaryWindowKey } from '../janus-engine/turns/legendary-window-action.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { type ZoneEndClaim, type ZoneEndReaders } from '../janus-engine/zones/end-of-turn-zones.js';
// Read-only reuse of the shared ability pipeline's contract + a pure page-held reader (never edited here —
// another agent owns janus-engine/ability/**): the tick's dice/charge/contest-retry resolution (janus-engine/turns/ticks.ts)
// replays the SAME handlers ability-truth.ts already serves with (damage-type rolls, skill rolls, charge adjusts), so it
// needs the identical JanusReaders shape ability-truth.ts builds for its own `readers()`.
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { abilityVisibility } from '../janus-engine/ability/visibility.js';
import { statusItemId, STATUS_IDS } from '../janus-engine/statuses/expiry.js';
import { applyTurnTick, prepareTurnTick, provisionalTurnTick, deferredTurnTick } from '../janus-engine/turns/tick-frame.js';

import { DYING_ROUND_DEFINITION_IDS } from '../janus-engine/turns/k4-dying.js';
import { heldInventoryRows } from '../janus-engine/statuses/gm-boundary.js';
import { ORB_TYPES, normalizeOrbQueue } from '../janus-engine/ability/orbs/orb-queue.js';
import { ecId, ecLean, type CampaignClock, type JanusReaders } from '../janus-engine/ability/context.js';
import { heldLayerlessDefinition } from '../janus-engine/compile/held-definition.js';
import { inputContent, startInputSnapshot, startLifecycleSource, statusInputSnapshot } from '../janus-engine/turns/input-snapshots.js';
import { collectImmunityDefinitionInputs, type ImmunityDefinitionInputs } from '../janus-engine/turns/immunity-definition-inputs.js';
import { refuseUnported } from '../janus-engine/unported.js';
import { orbOperationId } from '../janus-engine/turns/orb-boundary.js';
import { prepareDeathResources } from '../shared/spawn-browser.js';
import { planMaintainedUpkeep, maintainedExpiryStatuses, departedMaintainedStatuses } from '../janus-engine/turns/k4-upkeep.js';
import { removalSummons } from '../janus-engine/death/compose.js';
import { deathHolders } from '../janus-engine/death/departure.js';
import { deathStatusRows } from '../janus-engine/death/cleanup.js';
import { deathDutyName } from '../janus-engine/death/skip.js';
import { spawnDepartureIds } from '../janus-engine/spawn/row-plan.js';
import { retireEncounterCreatures } from './creature-departure.js';
/** The page's object cache (FateTide.objectCache — typed locally: the shared window.FateTide declaration omits it). */
const pageObjectCache = (): Map<string, unknown> | undefined => (window as unknown as { FateTide?: { objectCache?: Map<string, unknown> } }).FateTide?.objectCache;

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);
const ENDPOINT = '/options/game/actions/turn_truth_commit_inc.php';
// Include every AbilityType::getAll() bucket checked by the server turn hooks.
// Omitting ordinary `ability` entries lets a depleted recharge (e.g. Fleshdobbin
// Shriek) commit as a plain turn and silently bypass its roll and log.
const ABILITY_TYPES = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine', 'ability_choice', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known'];
const TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];

class TurnInputUnavailable extends Error { constructor(what: string) { super(what); this.name = 'TurnInputUnavailable'; } }
function definitionFor(row: Row | null, encounter: Row | null, id: string): Row | null {
  const full = (entry: unknown): Row | null => { const def = asRow(entry); return heldLayerlessDefinition(def) ? def : null; };
  const key = upper(id);
  const held = full(asRow(row?.catalog)?.[key]) ?? full(asRow(encounter?.catalog)?.[key]) ?? full(pageObjectCache()?.get(key)) ?? full(pageObjectCache()?.get(id));
  if (held) return held;
  for (const member of normalizePhpListish(encounter?.encounter_creatures_arr)) {
    const definition = full(asRow(asRow(member)?.catalog)?.[key]);
    if (definition) return definition;
  }
  return null;
}
function campaignFor(encounter: Row): Row {
  const campaign = heldCampaignFor(encounter);
  if (!campaign) throw new TurnInputUnavailable('campaign-unavailable');
  return campaign;
}
function heldCampaignFor(encounter: Row): Row | null {
  const id = upper(encounterField(encounter, 'encounter_master_parent_id'));
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const candidates = [asRow(id ? ft?.campaignCache?.get(id) : null), asRow(ft?.currentCampaignArr)];
  return candidates.find(c => c && id && upper(_ftCamField(c, 'campaign_id')) === id) ?? null;
}
/** The derived campaign clock as the client shows it (S1+S2): the folded columns when the clock module publishes them, else the row. */
function clockNow(campaign: Row): { seconds: (cols: CampaignClockCols) => number; now: number } {
  const calendar = ftClockCalendarFromArr(campaign);
  const derived = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
  const cols: CampaignClockCols = derived && Number.isFinite(Number(derived.year)) ? { year: Number(derived.year), month: Number(derived.month), day: Number(derived.day), hour: Number(derived.hour), minute: Number(derived.minute), second: Number(derived.second) } : ftClockColsFromArr(campaign);
  return { seconds: c => ftCampaignNowSeconds(c, calendar), now: ftCampaignNowSeconds(cols, calendar) };
}
function abilityEntries(row: Row, encounter: Row): AbilityEntry[] {
  /** passive_ability_entry_is_active(): the stamp is PHP empty() — '0', 0, false, '', null, [] (a list reads as its first element) is
   *  INACTIVE (R-141, Codex 2026-10-07: JS truthiness read a '0' stamp as active). Twin of regeneration-entry-gate.ts passiveEntryInactive. */
  function phpEmptyStamp(raw: unknown): boolean {
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v === null || v === undefined || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0);
  }
  const out: AbilityEntry[] = [];
  for (const type of ABILITY_TYPES) {
    for (const entry of ftLayerEntries(row, type)) {
      const e = asRow(entry);
      const ref = e?.item_arr ?? e?.ability_arr;
      const nested = asRow(ref);
      // Match ability_entry_id: legacy ability_id, then item_arr UUID/nest,
      // then the renamed item_id reference or a bare definition.
      const id = upper(typeof entry === 'string' ? entry : (e?.ability_id
        || (typeof ref === 'string' ? ref : null)
        || ftItemsCol(nested, 'item_id') || e?.item_id || ftItemsCol(e, 'item_id') || ''));
      if (!id) continue;
      const inline = nested ?? e;
      const def = inline && asRow(inline.item_layer_types) ? inline : definitionFor(row, encounter, id);
      // PHP ability_entry_{max_,}charges reads the modern cluster first, then
      // legacy fields, then the catalog default. Keep explicit zero/null values.
      const chargesObj = asRow(e?.charges);
      const catalogCharges = def ? ftLayerValue(def, 'ability_charges') : null;
      const charges = chargesObj && 'current' in chargesObj ? chargesObj.current
        : e && 'ability_charges' in e ? e.ability_charges
        : (!chargesObj ? e?.charges : null) ?? catalogCharges;
      const max = chargesObj && 'max' in chargesObj ? chargesObj.max
        : e && 'max_ability_charges' in e ? e.max_ability_charges
        : e?.ability_charges_max ?? e?.max_charges ?? catalogCharges;
      out.push({ id, type, definition: def, charges: charges === null || charges === undefined || charges === '' ? null : Number(scalar(charges)), max: max === null || max === undefined || max === '' ? null : Number(scalar(max)), inactive: !!e && 'passive_conditions_active' in e && phpEmptyStamp(e.passive_conditions_active) });
    }
  }
  return out;
}
function carriesKey(row: Row, encounter: Row, key: string): boolean {
  for (const type of TRIGGER_CARRIERS) {
    for (const entry of ftLayerEntries(row, type)) {
      const e = asRow(entry); const nested = asRow(e?.item_arr ?? e?.ability_arr ?? e?.status_arr ?? entry);
      const def = nested && asRow(nested.item_layer_types) ? nested : definitionFor(row, encounter, upper(typeof entry === 'string' ? entry : (e?.ability_id ?? e?.item_id ?? ftItemsCol(nested, 'item_id'))));
      if (!def || ftLayerEntries(def, key).length > 0) return true; // unresolvable carrier = unknown = a gate
    }
  }
  for (const status of ftCreatureStatuses(row, 'target') as Row[]) {
    const def = asRow(status.status_arr) ?? definitionFor(row, encounter, upper(status.status_item_id));
    if (!def || ftLayerEntries(def, key).length > 0) return true;
  }
  return false;
}
/** Exported for the T4 time-push adapter (websocket/time-truth.ts): the same definitions / derived clock / peer walk / ability entries over the held encounter. */
export function turnReaders(encounter: Row, clock?: CampaignClockCols, consent?: PageTriggerConsent): EndTurnReaders { return readers(encounter, consent, undefined, clock); }
// Death only needs turn inputs when it actually advances the pointer. Membership/order scans
// read saved columns; require a compiled member when the turn resolver reaches its row.
const turnDeathForMembers: NonNullable<JanusReaders['turnDeath']> = (encounter, members, dyingId) => {
  if (Number(encounterField(encounter, 'in_combat') ?? 0) !== 1) return { kind: 'none', why: 'not-in-combat' };
  if (upper(encounterField(encounter, 'current_encounter_creature_id_turn')) !== upper(dyingId)) return { kind: 'none', why: 'not-turn-holder' };
  const scene: Row = retainHeldBase(encounter, { ...encounter, encounter_creatures_arr: [...members.values()] });
  const encounterId = String(encounterField(encounter, 'encounter_id') ?? '');
  const requireRow = (id: string, member: Row | null): void => {
    if (entityCompileReady([id], false)) return;
    const name = member ? deathDutyName(member) : 'Encounter';
    throw new TurnInputUnavailable(`turn-death:entity-base-unavailable:${name} (${id}):${String(member?._entity_refusal ?? 'base-not-held')}`);
  };
  try {
    requireRow(encounterId, null);
    const read = readers(scene), rowFor = read.rowFor;
    read.rowFor = id => { const member = rowFor(id); if (member) requireRow(id, member); return member; };
    return resolveTurnDeath({ encounterId, dyingId }, scene, read);
  } catch (error) {
    if (error instanceof TurnInputUnavailable) return { kind: 'handback', reason: error.message };
    throw error;
  }
};
registerTurnDeathReader(turnDeathForMembers);
function readers(encounter: Row, consent?: PageTriggerConsent, alreadyResolved?: ZoneEndReaders['alreadyResolved'], roundClock?: CampaignClockCols): EndTurnReaders {
  const roster = (): Row[] => (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []);
  const rowFor = (id: string): Row | null => roster().find(r => upper(asRow(r.encounter_creatures)?.encounter_creature_id) === upper(id)) ?? null;
  const clock = clockNow(campaignFor(encounter));
  if (roundClock) clock.now = clock.seconds(roundClock);
  const skillCatalog = (): Record<string, unknown> | undefined => (window as unknown as { SKILL_CATALOG?: Record<string, unknown> }).SKILL_CATALOG;
  const resultRead: EndTurnReaders = {
    definition: id => definitionFor(null, encounter, id),
    value: (row, key) => ftLayerValue(row, key),
    entries: (row, key) => ftLayerEntries(row, key),
    seconds: clock.seconds, nowSeconds: clock.now,
    waterWetHeld: row => waterWetHeld(encounter, row, 1, { value: resultRead.value, entries: resultRead.entries,
      definition: resultRead.definition, statuses: actor => ftCreatureStatuses(actor, 'target') as Row[] }),
    rowFor,
    allStatusRows: () => roster().flatMap(r => [...(ftCreatureStatuses(r, 'target') as Row[]), ...(ftCreatureStatuses(r, 'caster') as Row[])]),
    zoneStatusRows: () => [encounter, ...heldInventoryRows({ encounter, members: new Map(roster().map(row => [ecId(row), row])) }).values()]
      .flatMap(row => ftCreatureStatuses(row, 'target') as Row[]),
    carriesLossTrigger: row => carriesKey(row, encounter, 'triggers_when_caster_loses_status_effect'),
    vitality: row => { const ec = asRow(row.encounter_creatures) ?? {}; const v = asRow(asRow(row.computed)?.vitality); return { current: Number(scalar(v?.v_current ?? ec.v_current) ?? 0), max: Number(scalar(v?.v_max ?? ec.v_max) ?? 0) }; },
    roster,
    inCombat: row => creatureIsInActiveCombat(encounter, row as never),
    abilityEntries: row => abilityEntries(row, encounter),
    carriesTurnBeginTrigger: row => carriesKey(row, encounter, 'triggers_when_caster_turn_begins'),
    skillVariable: id => { const entry = asRow(skillCatalog()?.[id] ?? skillCatalog()?.[upper(id)]); const v = String(entry?.variable_name ?? ''); return v === '' ? null : v; },
    uuid: () => crypto.randomUUID().toUpperCase(),
    // Serves the tick lane (dot / contest-retry / ability-recharge / ability-decay / psi-regen / turn-begin-trigger):
    // resolveEndTurn only widens its supported gate set when this is present (janus-engine/turns/end-turn.ts's
    // tickGates). No flag, no opt-in — providing it IS what serves those gates in the browser (owner: port, no switch).
    // OWNER RULING 2026-10-02 (trigger-consent.ts): an End Turn's optional triggers (Fortunate at a turn's start) ask the controller first
    get tick() {
      const tick = { roll: browserRollSource(), ability: { ...tickAbilityReaders(encounter, roundClock), ...(consent ? { triggerConsent: consent } : {}) } };
      Object.defineProperty(resultRead, 'tick', { value: tick, writable: true, enumerable: true, configurable: true });
      return tick;
    },
    // R-026 plan F4 slice 1 (lane A11): the ending actor's persistent-zone end-of-turn hooks, judged on the page's zone twin (zone-truth.ts)
    zoneEndOfTurn: endingId => endOfTurnZonesFor(encounter, endingId, tickAbilityReaders(encounter, roundClock), alreadyResolved),
    prepareTick: tick => {
      prepareTickInputs(encounter, tick);
    },
    startSource: (status, ending) => startLifecycleSource(status, ending, encounter, { definition: id => definitionFor(ending, encounter, id) }),
  };
  return resultRead;
}

/** The JanusReaders the tick's reused ability handlers (damage-type roll, skill roll, adjust-charges, janus()) need —
 * the SAME shape ability-truth.ts's own `readers()` builds, over this file's already-held encounter + roster.
 * `reactionInventoryComplete` is always true here: unlike an ability-use TARGET (which may need a fresh fetch to prove
 * its reaction inventory is complete), every tick participant is a roster member of the encounter the browser already
 * holds fully compiled (the same assumption turnReaders()/abilityEntries() above make). `deadState` stays null — the
 * ability pipeline's own ecLean() fallback resolves encounter_creature_dead straight off the row for a real member. */
function tickAbilityReaders(encounter: Row, roundClock?: CampaignClockCols): JanusReaders {
  const footprints = placedReaders(encounter, { targetId: '' }, null);
  const camp = campaignFor(encounter);
  const calendar = ftClockCalendarFromArr(camp);
  const cols = (): CampaignClock => {
    if (roundClock) return roundClock;
    const derived = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
    return derived && Number.isFinite(Number(derived.year))
      ? { year: Number(derived.year), month: Number(derived.month), day: Number(derived.day), hour: Number(derived.hour), minute: Number(derived.minute), second: Number(derived.second) }
      : ftClockColsFromArr(camp);
  };
  const def = (id: string): Row | null => definitionFor(null, encounter, id);
  return {
    turnDeath: turnDeathForMembers,
    auraOccupancy: row => footprints.occupancy(row),
    visibility: (viewer, target) => abilityVisibility(encounter, viewer, target),
    reactionInventoryComplete: () => true,
    deadState: () => null,
    inventoryField: (row, key) => row ? ftInventoryField(row, key) : null,
    value: (row, key) => ftLayerValue(row, key),
    has: (row, key) => !!row && (ftLayerValue(row, key) != null || (ftLayerEntries(row, key) ?? []).length > 0),
    entries: (row, key) => row ? ftLayerEntries(row, key) ?? [] : [],
    statuses: row => (ftCreatureStatuses(row, 'target') ?? []).flatMap((entry: unknown) => {
      const s = asRow(entry); if (!s) return [];
      const statusDef = asRow(s.status_arr) ?? def(upper(s.status_item_id));
      return [{ ...s, ...(asRow(s.status_arr) ? {} : statusDef ? { status_arr: statusDef } : {}) }];
    }),
    definition: def,
    inCombat: (enc, row) => creatureIsInActiveCombat(enc, row as never),
    houseRules: () => campaignHouseRules(camp),
    // R-197 (2026-10-07): a regeneration passive's light gate — only when the page holds the encounter's light geometry (members +
    // inventories); without it the reader is absent and the gate stays a hand-back (a [base]-only answer could differ from PHP's).
    ...(encounter.encounter_creatures_arr !== undefined && encounter.encounter_inventories_arr !== undefined
      ? { lightTypesPresent: (holder: Row, members?: readonly Row[]) => {
          // the tick's current member clones REPLACE their snapshot rows (by id; rows the ctx does not hold stay) — same container shape
          const orig = encounter.encounter_creatures_arr;
          const byId = new Map((members ?? []).map(m => [upper(asRow(m.encounter_creatures)?.encounter_creature_id), m] as const));
          const swap = (r: unknown): unknown => byId.get(upper(asRow(asRow(r)?.encounter_creatures)?.encounter_creature_id)) ?? r;
          const patched = !members ? orig : Array.isArray(orig) ? orig.map(swap)
            : asRow(orig) ? Object.fromEntries(Object.entries(asRow(orig)!).map(([k, r]) => [k, swap(r)])) : orig;
          return encounterLightTypesPresent({ ...encounter, encounter_creatures_arr: patched }, camp, asRow(holder.encounter_creatures)?.encounter_light);
        } } : {}),
    campaignClock: () => calendar.months.length ? cols() : null,
    addCampaignSeconds: (clock, seconds) => ftCampaignTimeAddSeconds(clock, calendar, seconds),
    uuid: () => crypto.randomUUID().toUpperCase(),
  };
}

const frameOf = (command: ActorTruthCommandBase): EndTurnFrame => (command as unknown as EndTurnCommand).frame;
const nextOf = (command: ActorTruthCommandBase): string => upper((command as unknown as EndTurnCommand).next_id);
const tickOf = (command: ActorTruthCommandBase): TurnTickResult | undefined => (command as unknown as EndTurnCommand).tick;
/** Every id the tick's OWN resolution touched beyond the ending/next actors already in the frame's `others` — a
 * contest-retry's caster, a dot's target when it differs from the ending actor, a status add/remove/change target,
 * a charge holder. Folding these into the shared `others` set keeps projection and rendering on the same participant set. */
function tickParticipantIds(tick: TurnTickResult, encounter: Row | null): string[] {
  const ids = new Set<string>();
  for (const id of Object.keys(tick.ec)) ids.add(upper(id));
  for (const s of tick.statusesAdded) { ids.add(upper(s.target)); ids.add(upper(s.caster)); }
  const held = new Set(normalizePhpListish(encounter?.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row).map(ecId));
  for (const s of tick.statusesRemoved) {
    ids.add(upper(s.target));
    if (held.has(upper(s.caster))) ids.add(upper(s.caster));
  }
  for (const c of tick.charges) ids.add(upper(c.holder));
  return [...ids];
}
/** Delivery metadata, with no before-image of encounter state. */
const pending = new Map<string, { tick?: TurnTickResult }>();
// Removal evidence disappears after apply. Keep the same touched rows through render, acceptance, and rollback.
const tickProjectionParticipants = new Map<string, string[]>();
const seen = new Set<string>();
function participants(command: ActorTruthCommandBase): { ending: Row | null; next: Row | null; others: Row[]; encounter: Row | null } {
  const encounter = actorEncounterFor(command.encounter_id);
  const actorId = upper(command.actor_id), nextId = nextOf(command);
  const tick = tickOf(command);
  const otherIds = new Set((frameOf(command).others ?? []).map(o => upper(o.actor_id)));
  if (tick) {
    const ids = [...new Set([...(tickProjectionParticipants.get(command.operation_id) ?? []), ...tickParticipantIds(tick, encounter)])];
    tickProjectionParticipants.set(command.operation_id, ids);
    if (tickProjectionParticipants.size > 128) tickProjectionParticipants.delete(tickProjectionParticipants.keys().next().value!);
    for (const id of ids) if (id !== actorId && id !== nextId) otherIds.add(id);
  }
  const others = [...otherIds].map(id => actorRowFor(command.encounter_id, id) as Row | null).filter((r): r is Row => r !== null);
  return { ending: actorRowFor(command.encounter_id, actorId) as Row | null, next: actorRowFor(command.encounter_id, nextId) as Row | null, others, encounter };
}
function remember(command: ActorTruthCommandBase): void {
  pending.set(command.operation_id, { tick: tickOf(command) });
  if (pending.size > 128) pending.delete(pending.keys().next().value!);
}
/** Turn-state event from the encounter AS IT STANDS (after a correction that is the authoritative pointer, never the
 * rejected command's — Codex B review F4). */
function render(command: ActorTruthCommandBase, applied = true, allowed?: ReadonlySet<string>): void {
  const p = participants(command);
  for (const row of new Set([p.ending, p.next, ...p.others])) if (row && (!allowed || allowed.has(ecId(row)))) handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  const enc = p.encounter;
  const bucket = asRow(enc?.encounters);
  window.dispatchEvent(new CustomEvent('ft:encounter-turn-state', { detail: { encounterId: command.encounter_id, encounterArr: enc, currentTurnId: upper(scalar(bucket?.current_encounter_creature_id_turn)), inCombat: Number(scalar(bucket?.in_combat) ?? 0) === 1, turnPhase: String(scalar(bucket?.turn_phase) ?? 'main'), source: 'turn-truth' } }));
  if (applied) window.dispatchEvent(new CustomEvent('ft:turn-truth-applied', { detail: { command, get applied_frame() { return appliedFrameOf(command); } } }));
}
/** Shared committed turn/calendar cleanup; existing row-patch subscribers own scene and action-draft retirement. */
export function retireTickCreatures(encounterId: string, tick: TurnTickResult): void {
  retireEncounterCreatures(encounterId, spawnDepartureIds(tick.spawnRows));
}
/** Shared projection of the native tick; the surrounding turn frame owns initiative and resource resets. */
function applyTick(command: ActorTruthCommandBase, committed: boolean | 'physical' = false, allowed?: ReadonlySet<string>): void {
  const tick = tickOf(command), encounter = actorEncounterFor(command.encounter_id);
  if (tick && encounter) applyTurnTick(normalizePhpListish(encounter.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row && (!allowed || allowed.has(ecId(row)))),
    committed === 'physical' ? deferredTurnTick(tick) : committed ? tick : provisionalTurnTick(tick, command.encounter_id), id => definitionFor(null, encounter, id), encounter);
  if (tick && committed) retireTickCreatures(command.encounter_id, tick);
}
function applyClock(encounter: Row | null, frame: TurnCommitFrame): void {
  if (!encounter || !frame.campaign_clock) return;
  // A scene can receive a correction before its campaign is adopted. There is
  // no local clock to patch yet; ordinary adoption supplies the saved checkpoint.
  // Keep participant repair independent of that optional projection.
  const campaign = heldCampaignFor(encounter);
  if (!campaign) return;
  if (applyTurnCommitClock(campaign, frame)) (window as unknown as { ftCampaignClock?: { captureBlob: (campaign: Row) => void } }).ftCampaignClock?.captureBlob(campaign);
}
function apply(command: ActorTruthCommandBase, revs: { ending?: number; next?: number }): void {
  const p = participants(command);
  if (p.encounter) for (const claim of (command as unknown as EndTurnCommand).zone_dedupe_claims ?? [])
    resolvedZoneClaims.add(zoneClaimKey(p.encounter, claim));
  applyEndTurnTruth({ ending: p.ending, next: p.next, others: p.others }, p.encounter, frameOf(command), revs, id => definitionFor(null, p.encounter, id));
  applyClock(p.encounter, frameOf(command) as TurnCommitFrame);
  applyTick(command);
}
/** Auto-advance waits for delivery and retries network failures with identical bytes. */
const legendaryAdvanceAttempts = new Map<string, number>();
async function post(command: ActorTruthCommandBase): Promise<unknown> {
  const attempts = legendaryAdvanceAttempts.get(command.operation_id);
  if (attempts === undefined) return postOnce(command);
  for (let attempt = 0; attempt < attempts; attempt++) {
    try { return await postOnce(command); }
    catch (error) {
      if (error instanceof MovementTruthRejected || error instanceof MovementTruthTerminal) throw error;
      if (attempt + 1 === attempts)
        throw new MovementTruthTerminal(0, String(error instanceof Error ? error.message : error), { legendary_advance_failed: true });
    }
  }
  throw new Error('legendary-window:delivery-attempts-unavailable');
}
async function postOnce(command: ActorTruthCommandBase): Promise<unknown> {
  const response = await saveAppliedTruth(ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withLogParticipants(command)), signal: AbortSignal.timeout(30000) });
  let result: Row | null = null;
  try { result = await response.json() as Row; } catch { result = null; }
  const rolledBackStatusConflict = response.status === 400
    && ['removal-not-expirable', 'removal-disagrees-with-row'].includes(String(result?.why ?? ''));
  if (!response.ok && (response.status === 409 || rolledBackStatusConflict) && Number.isInteger(asRow(asRow(result?.authoritative)?.rows)?.[command.actor_id] ? Number(asRow(asRow(asRow(result?.authoritative)?.rows)?.[command.actor_id])?.movement_rev) : NaN)) {
    const rev = Number(asRow(asRow(asRow(result?.authoritative)?.rows)?.[command.actor_id])?.movement_rev);
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: Number(row?.position?.x_pos ?? 0), y_pos: Number(row?.position?.y_pos ?? 0), z_pos: Number(row?.position?.z_pos ?? 0) };
    // The shared outbox recognizes error='stale' as terminal. Keep the detailed
    // rollback reason in `why`; putting it in `error` strands later actor commands.
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev, position, authoritative: result?.authoritative, why: result?.why } as unknown as MovementTruthRejectedResult);
  }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev)) {
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: Number(row?.position?.x_pos ?? 0), y_pos: Number(row?.position?.y_pos ?? 0), z_pos: Number(row?.position?.z_pos ?? 0) };
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev: Number(result.rev), position } as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) return result;
  const message = String(result?.error ?? (response.statusText || 'End Turn delivery failed'));
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message, result as Record<string, unknown> | null);
  throw new Error(message);
}
/** Saved truth from the server (accepted / committed): install it under its revisions; the next actor's row banks its own. */
function installSaved(command: ActorTruthCommandBase, saved: Row, rev: number, nextRev: number, allowed?: ReadonlySet<string>, project = true): void {
  const p = participants(command);
  const frame = saved as unknown as EndTurnFrame;
  if (!frame.next || !frame.encounter) return;
  // The NEXT actor's component is fenced on ITS confirmed revision: a delayed frame must not roll a newer confirmed state of
  // that row back (Codex B review F2). The ending row and the encounter scalars install as before.
  const nextId = nextOf(command);
  const nextFenced = p.next && ((!allowed || allowed.has(nextId)) ? nextId !== command.actor_id && nextRev < confirmedRevFor(command.encounter_id, nextId, p.next as MovementTruthRow) : true);
  const pointer = upper(encounterField(p.encounter, 'current_encounter_creature_id_turn'));
  const departure = asRow(asRow(command)?.departure);
  const encounterAllowed = (!allowed || allowed.has(command.actor_id)) && (pointer === upper(command.actor_id) || pointer === nextId
    || (departure?.advance === true && pointer === upper(encounterField(departure.before, 'current_encounter_creature_id_turn'))));
  if (project) applyEndTurnTruth({ ending: !allowed || allowed.has(command.actor_id) ? p.ending : null, next: nextFenced ? null : p.next,
    others: p.others.filter(row => !allowed || allowed.has(ecId(row))) },
    encounterAllowed ? p.encounter : null,
    frame, { ending: rev, next: nextRev }, id => definitionFor(null, p.encounter, id));
  if (project && encounterAllowed) applyClock(p.encounter, frame as TurnCommitFrame);
  if (p.next && nextId !== command.actor_id && !nextFenced) { bankConfirmedRev(command.encounter_id, nextId, p.next as MovementTruthRow, nextRev,
    frame.next.refresh === false ? {} : { aps: Number(frame.next.aps), movement_pct: 0 }); reconcileActorRow(command.encounter_id, nextId, p.next as MovementTruthRow); }
}
/** Swaps this browser's own provisional tick status ids (minted before the optimistic apply) for the server's real
 * minted ones — same index, same technique ability-truth.ts's accepted() uses for a cast's own statusesAdded. */
function reconcileTickIds(command: ActorTruthCommandBase, savedTick: Row | null): void {
  const tick = tickOf(command);
  if (!tick?.statusesAdded.length || !savedTick || !Array.isArray(savedTick.statusesAdded)) return;
  const real = savedTick.statusesAdded as Row[];
  for (const [i, status] of tick.statusesAdded.entries()) {
    const provisional = upper((status as unknown as Row).status_id);
    const actual = upper(real[i]?.status_id);
    if (!actual || !provisional || actual === provisional) continue;
    for (const id of new Set([upper(status.target), upper(status.caster)])) {
      const member = actorRowFor(command.encounter_id, id) as Row | null;
      const statuses = asRow(member?.statuses);
      for (const mode of ['target', 'caster'] as const) {
        const list = statuses?.[mode];
        if (Array.isArray(list)) for (const entry of list as Row[]) if (upper(entry.status_id) === provisional) entry.status_id = actual;
      }
    }
  }
}
function accepted(command: ActorTruthCommandBase, result: MovementTruthAcceptedResult): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  if (!row) return;
  const r = result as unknown as Row;
  bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev, {});
  pending.delete(command.operation_id);
  const saved = asRow(r.frame);
  // The browser already applied this reset; a passive acknowledgement must not replay it over later actions.
  if (saved) installSaved(command, saved, result.rev, Number(r.next_rev ?? 0), undefined, false);
  applyTick(command, 'physical');
  reconcileTickIds(command, asRow(r.tick));
  reconcileActorRow(command.encounter_id, command.actor_id, row);
  render(command);
  window.dispatchEvent(new CustomEvent('ft:timed-finish-now', { detail: { encounterId: command.encounter_id } }));
  // OWNER RULING 2026-10-03: a luck gain inside the ticks that a player's creature did not spend → its controller's 5 s Fortunate offer
  const offers = fortunateOffersAfterCommit.get(command.operation_id);
  fortunateOffersAfterCommit.delete(command.operation_id);
  if (offers?.length) offerFortunate(command.encounter_id, command.actor_id, offers);
}
/** OWNER RULING 2026-10-03 (Fortunate): the ticks' deferred luck-gain offers per End Turn operation, sent once its commit is accepted. */
const fortunateOffersAfterCommit = new Map<string, string[]>();
const turnRepairs = new Map<string, Promise<boolean>>();
/** Corrections reload only touched members; saved encounter scalars arrive on the refusal. */
function repairTurn(command: ActorTruthCommandBase, authoritative?: unknown): Promise<boolean> {
  const key = command.operation_id, saved = asRow(authoritative), encounter = actorEncounterFor(command.encounter_id);
  const scalars = asRow(saved?.encounter), actor = actorRowFor(command.encounter_id, command.actor_id);
  const savedActor = asRow(asRow(saved?.rows)?.[upper(command.actor_id)]);
  const current = !actor || !savedActor || Number(encounterCreatureField(savedActor, 'movement_rev') ?? 0) >= confirmedRevFor(command.encounter_id, command.actor_id, actor);
  if (current && encounter && scalars) Object.assign((encounter.encounters ??= {}) as Row, scalars);
  const clock = asRow(saved?.campaign_clock);
  if (current && encounter && clock) applyClock(encounter, { campaign_clock: clock } as TurnCommitFrame);
  const prior = turnRepairs.get(key);
  if (prior) return prior;
  const ids = new Set([upper(command.actor_id), nextOf(command),
    ...Object.keys(asRow(saved?.rows) ?? {}), ...(frameOf(command)?.others ?? []).map(other => upper(other.actor_id)),
    ...(tickProjectionParticipants.get(key) ?? []),
    ...(tickOf(command) ? tickParticipantIds(tickOf(command)!, encounter) : [])]);
  if (ftCurrentTruthSink()) return Promise.resolve(true);
  const repair = Promise.all([...ids].filter(Boolean).map(id => fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true, participantDeadline: true })))
    .then(() => {
      if (frameOf(command)?.next) render(command, false);
      else window.dispatchEvent(new CustomEvent('ft:encounter-turn-state', { detail: { encounterId: command.encounter_id, encounterArr: encounter, source: 'turn-truth' } }));
      return true;
    }).catch(error => { console.warn('End Turn participant repair failed', error); return false; });
  turnRepairs.set(key, repair);
  if (turnRepairs.size > 128) turnRepairs.delete(turnRepairs.keys().next().value!);
  return repair;
}
const turnRetries = new Map<string, { basis: string; repairing?: boolean }>();
const pendingEndTurns = new Map<string, string>();
async function settleEndTurnSave(command: ActorTruthCommandBase, completion: Promise<unknown>): Promise<unknown> {
  const key = upper(command.encounter_id);
  pendingEndTurns.set(key, command.operation_id);
  const settled = completion.finally(() => {
    if (pendingEndTurns.get(key) === command.operation_id) pendingEndTurns.delete(key);
  });
  try { return await withAsyncDeadline(() => settled, 35000); }
  catch {
    // The outbox still owns these bytes. An uncertain save is never replanned.
    refuseUnported('end-turn:save-unconfirmed', { encounterId: command.encounter_id, actorId: command.actor_id },
      'End Turn is still awaiting confirmation. Check the current turn and its delivery notice before trying again.');
    window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { command, reason: 'end-turn:save-unconfirmed' } }));
    return null;
  }
}
/** Only a rolled-back revision conflict can retry a retained plain boundary.
 * A new wire identity is necessary: peers have retired the refused operation. */
function retryableEndTurn(command: ActorTruthCommandBase, result: unknown): Row | null {
  const answer = asRow(result), saved = asRow(answer?.authoritative), encounter = asRow(saved?.encounter);
  const before = (command as unknown as EndTurnCommand).before;
  if (!turnRetries.has(command.operation_id) || answer?.error !== 'stale' || answer?.why !== 'stale'
    || !saved || !encounter || Number(encounterField(encounter, 'in_combat') ?? 0) !== 1
    || upper(encounterField(encounter, 'current_encounter_creature_id_turn')) !== upper(command.actor_id)
    || Number(encounterField(encounter, 'current_round') ?? 0) !== before.round
    || String(encounterField(encounter, 'turn_phase') ?? 'main') !== (before.turn_phase ?? 'main')
    || String(encounterField(encounter, 'pending_main_turn_encounter_creature_id') ?? '') !== before.pending_main_turn_encounter_creature_id
    || String(encounterField(encounter, 'legendary_turn_queue') ?? '') !== before.legendary_turn_queue) return null;
  return saved;
}
async function recoverEndTurn(command: ActorTruthCommandBase, result: unknown): Promise<EndTurnCommitOutcome> {
  const retry = turnRetries.get(command.operation_id), saved = retryableEndTurn(command, result);
  if (!retry?.repairing || !saved) return 'refused';
  const refuse = (reason: string): EndTurnCommitOutcome => {
    refuseUnported(reason, { encounterId: command.encounter_id, actorId: command.actor_id }, reason === 'end-turn:repair-timeout'
      ? 'End Turn could not refresh its participants in time. Check the current turn before trying again.'
      : 'End Turn was not saved because its participants changed. Check the current turn before trying again.');
    window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { command, reason, result } }));
    return 'refused';
  };
  try { if (!await withAsyncDeadline(() => repairTurn(command, saved), 20000)) return refuse('end-turn:repair-unavailable'); }
  catch { return refuse('end-turn:repair-timeout'); }
  const encounter = actorEncounterFor(command.encounter_id), rows = asRow(saved.rows);
  // The repair must have adopted these exact confirmed participant revisions.
  // A later action, unresolved queue or changed rule input is a real conflict.
  if (!encounter || !entityCompileReady([command.encounter_id, command.actor_id, nextOf(command)])
    || plainTurnRetryBasis(encounter) !== retry.basis || !rows
    || pendingFor(command.encounter_id, command.actor_id) !== 0
    || pendingFor(command.encounter_id, nextOf(command)) !== 0) return refuse('end-turn:participants-changed');
  const actor = actorRowFor(command.encounter_id, command.actor_id), next = actorRowFor(command.encounter_id, nextOf(command));
  const actorRev = Number(encounterCreatureField(asRow(rows[command.actor_id]), 'movement_rev'));
  const nextRev = Number(encounterCreatureField(asRow(rows[nextOf(command)]), 'movement_rev'));
  if (!actor || !next || !Number.isInteger(actorRev) || !Number.isInteger(nextRev)
    || confirmedRevFor(command.encounter_id, command.actor_id, actor) !== actorRev
    || confirmedRevFor(command.encounter_id, nextOf(command), next) !== nextRev) return refuse('end-turn:participants-changed');
  const replay = { ...command, operation_id: crypto.randomUUID().toUpperCase(), expected_rev: actorRev, next_expected_rev: nextRev };
  // Enqueue the retained frame/tick once. Never run the resolver or draw again.
  // This operation receives no retry marker, so a second CAS conflict terminates.
  try {
    const completion = actorOutbox(command.encounter_id, command.actor_id).enqueue(withLogParticipants(replay)).commit;
    noteOwnOperation(replay.operation_id, END_TURN_SCHEMA); seen.add(replay.operation_id);
    return isMovementTruthAcceptedResult(await settleEndTurnSave(replay, completion)) ? 'handled' : 'refused';
  } catch { return refuse('end-turn:recovery-save-failed'); }
}
function rejected(command: ActorTruthCommandBase, result: MovementTruthRejectedResult): void {
  fortunateOffersAfterCommit.delete(command.operation_id);
  pending.delete(command.operation_id);
  void repairTurn(command, asRow(result)?.authoritative);
  if (retryableEndTurn(command, result)) { turnRetries.get(command.operation_id)!.repairing = true; return; }
  if (ftCurrentTruthSink()) {
    if (typeof displayWarning === 'function') displayWarning(trainingGroundsRefusalMessage(result.error, 'End Turn'));
  } else {
    const pointer = upper(encounterField(asRow(asRow(asRow(result)?.authoritative)?.encounter), 'current_encounter_creature_id_turn'));
    refuseUnported(pointer && pointer !== upper(command.actor_id) ? 'end-turn:turn-already-moved' : 'end-turn:participants-changed',
      { encounterId: command.encounter_id, actorId: command.actor_id, why: asRow(result)?.why },
      pointer && pointer !== upper(command.actor_id) ? 'Another player has already moved the turn. Check the current turn before ending it.'
        : 'End Turn was not saved because its participants changed. Refreshing those participants; check the current turn before trying again.');
  }
  window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { command, result } }));
}
function failed(command: ActorTruthCommandBase, info: MovementTruthTerminalInfo): void {
  fortunateOffersAfterCommit.delete(command.operation_id);
  pending.delete(command.operation_id);
  void repairTurn(command, asRow(info.body)?.authoritative);
  const refusal = refusalOf(info);
  if (typeof displayWarning === 'function') displayWarning('End Turn could not be saved (' + info.error + '). Refreshing its participants.');
  window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { command, info, refusal } }));
}
registerTruthSchema(END_TURN_SCHEMA, {
  apply: (row, command, rev) => {
    void row; remember(command); apply(command, { ending: rev, next: nextOf(command) === command.actor_id ? rev : (command as unknown as EndTurnCommand).next_expected_rev + (frameOf(command).next.refresh === false ? 0 : 1) });
    const newEntities = takeAppliedEntities(command.operation_id);
    exposeAppliedFrame(command, () => {
      const p = participants(command), typed = command as unknown as EndTurnCommand;
      const members = [...new Set([p.ending, p.next, ...p.others].filter((member): member is Row => member !== null))];
      const memberRevs = Object.fromEntries(members.map(member => {
        const id = ecId(member);
        return [id, id === command.actor_id ? rev : id === nextOf(command) ? typed.next_expected_rev + (frameOf(command).next.refresh === false ? 0 : 1)
          : Number(ecLean(member, 'movement_rev') ?? 0)];
      }));
      const frame = frameOf(command), tick = tickOf(command);
      const removed = new Set([...frame.ending.status_remove_ids, ...frame.next.status_remove_ids,
        ...frame.others.flatMap(other => other.status_remove_ids), ...(tick?.statusesRemoved ?? []).flatMap(status => status.status_id ? [status.status_id] : [])]);
      return memberAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id,
        actor_id: command.actor_id, rev, member_revs: memberRevs }, [...(p.encounter ? [p.encounter] : []), ...members],
        newEntities(), [...removed].map(row_id => ({ table: 'statuses', row_id })),
        [...counterspellWatchRows(p.encounter, command.actor_id, undefined, frame.next.refresh !== false ? [nextOf(command)] : []),
          ...appliedChargeRows(tick?.charges ?? [], members), ...appliedSpawnRows(tick?.spawnRows)]);
    });
  },
  render: command => render(command), commit: post, accepted, rejected, failed,
  handlesRejection: operationId => turnRetries.get(operationId)?.repairing === true,
  staleMessage: 'Your End Turn was rejected: the encounter changed elsewhere. Reloading the encounter.',
  computedRows: command => { const p = participants(command); return [p.ending, p.next, ...p.others].filter((member): member is Row => member !== null); },
});

const immunityDefinitionWarmups = new WeakMap<Row, { epoch: string; view: ImmunityDefinitionInputs; job: Promise<void> }>();
/** Join discovery for the same status/provider revision, including failed reads.
 * Missing inputs still reach expiry's existing named gate; warming never decides it. */
async function prefetchImmunityDefinitions(encounter: Row, roster: readonly Row[]): Promise<void> {
  const store = (globalThis as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
  const epoch = JSON.stringify([store?.epoch, store?.cacheGeneration]);
  const read = { definition: (member: Row, id: string) => definitionFor(member, encounter, id),
    statuses: (member: Row) => ftCreatureStatuses(member, 'target') as Row[],
    entries: (node: Row, key: string) => ftLayerEntries(node, key) as unknown[] };
  const prior = immunityDefinitionWarmups.get(encounter);
  const view = collectImmunityDefinitionInputs(roster, read, prior?.epoch === epoch ? prior.view : undefined);
  if (prior?.epoch === epoch && prior.view === view) return prior.job;
  const current = { epoch, view, job: Promise.resolve() };
  current.job = Promise.resolve().then(async () => {
    const attempted = new Set<string>();
    for (;;) {
      const missing = current.view.missing.filter(id => !attempted.has(id));
      if (!missing.length) return;
      missing.forEach(id => attempted.add(id));
      try { await fetchObjectsBatch(missing); }
      catch { /* Unavailable definitions remain missing for expiry's named gate. */ }
      current.view = collectImmunityDefinitionInputs(roster, read, current.view);
    }
  }).catch(error => {
    if (immunityDefinitionWarmups.get(encounter) === current) immunityDefinitionWarmups.delete(encounter);
    throw error;
  });
  immunityDefinitionWarmups.set(encounter, current);
  return current.job;
}
/** Adoption warms tick/trigger definitions. Turn decisions use only these held inputs. */
export async function prefetchTickDefinitions(encounter: Row, endingRow: Row): Promise<void> {
  const known = (id: string): boolean => !!definitionFor(endingRow, encounter, id);
  const zoneHolders = [encounter, ...(encounter.encounter_creatures_arr as Row[] ?? []), ...(encounter.encounter_inventories_arr as Row[] ?? [])];
  const zoneIds = zoneHolders.flatMap(holder => (ftCreatureStatuses(holder, 'target') as Row[] ?? [])
    .filter(s => Number(s.status_aoe_source) === 1 && !isSelfStatusOrigin(upper(s.status_id), upper(s.status_origin_id)))
    .map(s => upper(s.status_origin_id) || upper(s.status_item_id)));
  const targetStatusIds = [...new Set([...(ftCreatureStatuses(endingRow, 'target') as Row[]).map(s => upper(s.status_item_id)), ...zoneIds].filter(Boolean))];
  // A successor can be a lair, a queued boss, or the pending main actor. Warm
  // their START inputs as well as the ending row before the synchronous passes.
  const roster = encounter.encounter_creatures_arr as Row[] ?? [];
  const inventoryOrigins = heldInventoryRows({ encounter, members: new Map(roster.map(row => [ecId(row), row])) });
  const startIds = roster.flatMap(row => [
    ...abilityEntries(row, encounter).map(entry => entry.id),
    ...(ftCreatureStatuses(row, 'caster') as Row[]).map(statusItemId),
    ...(ftCreatureStatuses(row, 'target') as Row[]).map(statusItemId),
    ...(ftCreatureStatuses(row, 'target') as Row[]).map(status => upper(statusInputSnapshot(status).status_origin_id)),
  ]);
  if (roster.some(row => (ftCreatureStatuses(row, 'target') as Row[]).some(status => statusItemId(status) === '8AE129AF-52D8-CCE8-706A-85767DB52740')))
    startIds.push(...DYING_ROUND_DEFINITION_IDS);
  if (roster.some(row => Number(ecLean(row, 'luck')) > 0)) startIds.push('AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8');
  const fullStart = [...new Set(startIds)].filter(Boolean);
  // lane A10 (R-026 plan F2): the ENDING actor's channeled orbs pulse through their authored abilities (ticks.ts orbEndOfTurnPass) — those
  // definitions and what they apply / deal (a damage type, a status) are fetched here like the DoT's; the tick hands back on what it still lacks
  const channels = (encounter.encounter_creatures_arr as Row[] ?? []).some(row =>
    (ftCreatureStatuses(row, 'target') ?? []).some((s: Row) => {
      const def = asRow(s.status_arr) ?? definitionFor(row, encounter, statusItemId(s));
      // A cold status definition may channel. Warm its orb payload in this same batch.
      return !def || Number(ftLayerValue(def, 'channel_orb_each_turn')) === 1;
    }));
  const orbIds = [...new Set([
    ...(normalizeOrbQueue(asRow(endingRow.encounter_creatures)?.orb_queue) ?? []).map(slug => ORB_TYPES.find(orb => orb.slug === slug)?.passive),
    ...(channels ? ORB_TYPES.map(orb => orb.evoke) : []),
  ].filter((id): id is string => !!id))];
  // …and the universal Fortunate (a natural 20 on a pulse's contest grants luck — caster_gains_luck reads its definition)
  if (orbIds.length) orbIds.push('AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8');
  await fetchObjectsBatch([...new Set([...fullStart, ...targetStatusIds, ...orbIds])]
    .filter(id => !known(id) && !inventoryOrigins.has(id)));
  // Zone anchors can live on the encounter or inventory, outside member listener discovery.
  // Their full referenced payload uses the same warm-up as cast/finish/turn-begin carriers.
  await hydrateTurnTriggerDefinitions(endingRow, encounter, [...new Set([...targetStatusIds, ...orbIds, ...fullStart])]
    .map(id => definitionFor(endingRow, encounter, id))
    .filter((def): def is Row => !!def));
  await prefetchImmunityDefinitions(encounter, roster);
}
/** The full held roster includes hidden members. Re-evaluate departures at each turn without network. */
function maintainedTargetsGone(encounter: Row): ReadonlySet<string> {
  const roster = normalizePhpListish(encounter.encounter_creatures_arr).map(asRow).filter((member): member is Row => !!member);
  const live = new Set(roster.filter(member => Number(ecLean(member, 'encounter_creature_deleted') ?? 0) !== 1).map(ecId));
  const targets = roster.flatMap(member => ftCreatureStatuses(member, 'caster') as Row[])
    .map(status => upper(statusInputSnapshot(status).status_target_id));
  return new Set(targets.filter(target => target && !live.has(target)));
}
/** Pure prerequisite extraction. Every synthetic tick outcome calls this before
 * resolveEndTurnTicks uses real dice; the actual command uses the same extractor.
 * Reads come from the captured roster, which the resolver never mutates. */
function prepareTickInputs(encounter: Row, tick: TurnTickResult): ReturnType<typeof prepareTurnTick> {
  return prepareTurnTick(encounter.encounter_creatures_arr as Row[] ?? [], tick, row => abilityEntries(row, encounter));
}
export type EndTurnCommitOutcome = 'handled' | 'refused';
/** A rule-input stamp for a boundary with no due effects. It is never
 * a rollback snapshot: no entity, catalog, computed cache or floor is copied.
 * Resource-only revisions (luck, for example) cannot change this boundary. */
function plainTurnRetryBasis(encounter: Row): string | null {
  const roster = normalizePhpListish(encounter.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row);
  const holders = [encounter, ...normalizePhpListish(encounter.encounter_inventories_arr).map(asRow).filter((row): row is Row => !!row)];
  if (holders.some(row => (ftCreatureStatuses(row, 'target')?.length ?? 0) > 0 || (ftCreatureStatuses(row, 'caster')?.length ?? 0) > 0)
    || roster.some(row => Number(ftLayerValue(row, 'legendary_creature')) === 1
      || Number(ftLayerValue(row, 'lair_creature')) === 1 || normalizeOrbQueue(ecLean(row, 'orb_queue'))?.length)) return null;
  const read = readers(encounter);
  const inputs: unknown[] = [], definitions = new Map<string, unknown>();
  try {
    for (const row of roster) {
      const entries = abilityEntries(row, encounter);
      if (entries.some(entry => !entry.definition)) return null;
      for (const entry of entries) if (!definitions.has(entry.id)) definitions.set(entry.id, inputContent(entry.definition!.item_layer_types));
      inputs.push([ecId(row), startInputSnapshot(row, read),
        entries.map(entry => [entry.id, entry.type, entry.charges, entry.max, entry.inactive])]);
    }
  } catch { return null; } // Unknown effect inputs retain the normal named refusal.
  return JSON.stringify([
    read.nowSeconds, [...definitions], inputs,
    ...['in_combat', 'current_round'].map(key => Number(encounterField(encounter, key) ?? 0)),
    ...['current_encounter_creature_id_turn', 'active_combat_id', 'pending_main_turn_encounter_creature_id', 'legendary_turn_queue'].map(key => upper(encounterField(encounter, key))),
    String(encounterField(encounter, 'turn_phase') ?? 'main'),
    ...roster.map(row => [ecId(row),
      ...['encounter_id', 'item_type', 'combat_id', 'controller_encounter_creature_id', 'encounter_creature_created_by_id'].map(key => upper(ecLean(row, key))),
      ...['creature_initiative', 'creature_number', 'encounter_creature_dead', 'encounter_creature_deleted', 'encounter_creature_private',
        'aps_total', 'aps', 'reaction', 'weapon_attack', 'head_attack', 'movement_pct'].map(key => Number(ecLean(row, key) ?? 0)),
      upper(ftLayerValue(row, 'permissioned')), read.vitality(row),
    ]),
  ]);
}
function plainEndTurnCommand(command: EndTurnCommand): boolean {
  const tick = command.tick;
  return !command.wrap && (command.before.turn_phase ?? 'main') === 'main' && command.frame.next.refresh !== false
    && command.frame.clock_seconds === 0 && !command.frame.skipped?.length && !command.frame.others.length
    && !command.writes.ending.status_remove.length && !command.writes.next.status_remove.length
    && !command.writes.others.length && !command.writes.ap_change_sources.length
    && !command.zone_inputs?.length && !command.zone_dedupe_claims?.length && !command.start_status_sources?.length
    && !!tick && tick.rows.length === 2 && tick.rows.every(row => ['end_turn', 'start_turn'].includes(String(row.roll_type_1)))
    && !tick.statusesAdded.length && !tick.statusesRemoved.length && !tick.statusesChanged.length && !tick.charges.length
    && !tick.spawnRows && !tick.teleportOffers?.length && !tick.radiusChanges?.length && !tick.fortunateOffers?.length
    && !tick.statusTriggerListeners?.length && !Object.keys(tick.statusFrames ?? {}).length
    && Object.entries(tick.ec).every(([id, changes]) => id === command.next_id
      && Object.keys(changes).every(key => key === 'reaction') && changes.reaction === command.frame.next.reaction);
}
const endingTurns = new Map<string, Promise<EndTurnCommitOutcome>>();
/** Handled boundaries mutate held rows, render and queue browser truth. */
export async function tryCommitEndTurn(input: { encounterId: string; actorId: string; rollPrivate?: number; legendaryWindowKey?: string }): Promise<EndTurnCommitOutcome> {
  const key = upper(input.encounterId), active = endingTurns.get(key);
  if (active) return active;
  if (pendingEndTurns.has(key)) {
    refuseUnported('end-turn:save-unconfirmed', { encounterId: key, actorId: input.actorId },
      'The previous End Turn is still awaiting confirmation. Check its delivery notice before ending another turn.');
    return 'refused';
  }
  const timing = startActionTiming('end-turn');
  const work = Promise.resolve().then(async () => {
    try {
      const outcome = await tryCommitEndTurnCore(input, undefined, timing);
      return outcome === 'failed' ? 'refused' : outcome;
    } finally { endingTurns.delete(key); endActionTiming(timing); }
  });
  endingTurns.set(key, work);
  return work;
}
async function advanceLegendaryWindow(input: { encounterId: string; actorId: string; legendaryWindowKey: string; retry?: boolean }): Promise<EndTurnCommitOutcome | 'failed'> {
  return tryCommitEndTurnCore(input, input.retry ? 1 : 2);
}
async function tryCommitEndTurnCore(input: { encounterId: string; actorId: string; rollPrivate?: number; legendaryWindowKey?: string }, deliveryAttempts?: number, timing?: string): Promise<EndTurnCommitOutcome | 'failed'> {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId);
  // A post-commit refetch may still be adopting these rows: join it, then read the verdict (hot4, ruling 125).
  if (!await awaitEntityActionReady([encounterId, actorId, ...normalizePhpListish((actorEncounterFor(encounterId) as Row | null)?.encounter_creatures_arr)
      .map(asRow).filter((member): member is Row => member !== null).map(ecId)], actorId, ['turn'])) return 'refused';
  markActionTiming(timing, 'decide_ms');
  const encounter = actorEncounterFor(encounterId), row = actorRowFor(encounterId, actorId);
  const windowChanged = (): boolean => input.legendaryWindowKey !== undefined
    && legendaryWindowKey(actorEncounterFor(encounterId) as Row | null) !== input.legendaryWindowKey;
  if (windowChanged()) return 'refused';
  if (!encounter || !row) { refuseUnported('end-turn:rows-unavailable'); return 'refused'; }
  confirmedRevFor(encounterId, actorId, row);
  const gone = maintainedTargetsGone(encounter);
  const userId = upper(window.FateTide?.user?.id), isGM = window.FateTide?.user?.isGM === true;
  const isStaff = ['employee', 'admin', 'super_admin'].includes(String((window.FateTide?.user as { primaryRole?: string } | undefined)?.primaryRole ?? ''));
  let scope;
  const consent = createTriggerConsent(encounterId);
  let maintenanceDeaths: JanusReaders['deathResources'];
  try {
    const roster = normalizePhpListish(encounter.encounter_creatures_arr) as Row[];
    // These planners only read. Retain the held scene rather than copying its catalogs/floor for an input probe.
    const members = new Map(roster.map(member => [ecId(member), member]));
    const ctx: import('../janus-engine/ability/context.js').JanusCtx = { encounter, members, originals: members,
      read: tickAbilityReaders(encounter), roll: browserRollSource(), rows: [], statusesAdded: [], statusesRemoved: [],
      chargesChanged: [], inventoryChanged: [], statusConnectedId: null, rollPrivate: 0 };
    const holder = ctx.members.get(actorId)!;
    ctx.read.maintainedTargetGone = id => gone.has(id);
    const sources = [...maintainedExpiryStatuses(ctx, holder, 'end'),
      ...roster.flatMap(member => maintainedExpiryStatuses(ctx, member, 'start')),
      ...roster.flatMap(member => departedMaintainedStatuses(ctx, member)),
      ...planMaintainedUpkeep(ctx, holder).filter(plan => plan.current <= plan.qty).flatMap(plan => plan.statuses)];
    const historical = deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()]))
      .filter(status => statusItemId(status) !== STATUS_IDS.summon_tag || !gone.has(upper(statusInputSnapshot(status).status_target_id)));
    if (sources.some(source => removalSummons(ctx, source, historical).length))
      maintenanceDeaths = await prepareDeathResources(roster, encounter, ctx.read, campaignFor(encounter));
  } catch (error) {
    console.warn('turn-truth: maintenance departure inputs unavailable', error);
  }
  // The browser owns the round's zone receipt ledger. Repeated boundaries on this page
  // read the decision already applied; no storage reservation precedes a local turn.
  const alreadyResolved: NonNullable<ZoneEndReaders['alreadyResolved']> = claim => {
    if (resolvedZoneClaims.has(zoneClaimKey(encounter, claim))) return true;
    const anchor = [encounter as Row, ...normalizePhpListish(encounter.encounter_creatures_arr) as Row[],
      ...normalizePhpListish(encounter.encounter_inventories_arr) as Row[]]
      .flatMap(holder => ftCreatureStatuses(holder, 'target') as Row[]).find(status => upper(status.status_id) === upper(claim.zone_status_id));
    if (!anchor) return false;
    try {
      const read = tickAbilityReaders(encounter);
      const origin = upper(statusInputSnapshot(anchor).status_origin_id) || statusItemId(anchor);
      const surface = hasSurfaceTypeValue(read, read.definition(origin));
      const records = surface ? readSurfaceEntryRecords(encounter) : readPaoeEntryRecords(encounter);
      return paoeEntryAlreadyResolved({ encounter, read }, {
        abilityId: claim.item_id, casterId: claim.caster_id, enteringId: claim.target_id,
        statusConnectedId: upper(statusInputSnapshot(anchor).status_connected_id), forcedExtreme: 0,
      }, records);
    } catch { return false; } // A late adoption/read race uses local truth; the next full read heals it (owner 147).
  };
  for (;;) {
    try {
      if (windowChanged()) return 'refused';
      const read = readers(encounter, consent, alreadyResolved);
      if (read.tick) read.tick.ability.maintainedTargetGone = id => gone.has(id);
      if (read.tick && maintenanceDeaths) read.tick.ability.deathResources = maintenanceDeaths;
      scope = resolveEndTurn({ encounterId, actorId, userId, isGM, isStaff, rollPrivate: input.rollPrivate ?? 2 }, encounter, read);
    } catch (error) {
      refuseUnported('end-turn:resolver-exception', { error: String(error instanceof Error ? error.message : error) });
      return 'refused';
    }
    // the dry runs met an optional trigger on a player-controlled creature: ask its controller (≤ 5 s), then resolve with the answers fixed
    if (!scope.supported && !scope.refused && !consent.final && scope.reason === `preflight:${TRIGGER_CONSENT_REASON}`) { await askTriggerConsent(encounterId, actorId, consent); continue; }
    break;
  }
  if (!scope.supported) {
    if (!scope.refused && scope.reason === 'gm-actor-not-pointer') {
      refuseUnported('end-turn:gm-actor-not-pointer', { encounterId, actorId },
        'The turn has moved to another creature. Check the current turn before ending it.');
    } else if (scope.refused) {
      if (typeof displayWarning === 'function') displayWarning(scope.message);
    } else refuseUnported('end-turn:' + scope.reason, { reasons: scope.reasons });
    window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { input, scope } }));
    return 'refused';
  }
  const outbox = actorOutbox(encounterId, actorId);
  const command = turnCommitFrame(scope.command, campaignFor(encounter)) as unknown as ActorTruthCommandBase;
  const retryBasis = deliveryAttempts === undefined && !ftCurrentTruthSink() && plainEndTurnCommand(scope.command) ? plainTurnRetryBasis(encounter) : null;
  const boundary = { actor_id: scope.command.next_id, round: scope.command.frame.encounter.current_round,
    combat_id: upper(asRow(encounter.encounters)?.active_combat_id) };
  if (scope.command.frame.next.refresh !== false)
    (command as unknown as Row).orb_channel_claim = { ...boundary, operation_id: await orbOperationId('channel', boundary) };
  // OWNER RULING 2026-10-03: the ticks' deferred Fortunate offers are a page-side field — never posted; offered once the commit is accepted
  const deferred = tickOf(command)?.fortunateOffers;
  if (deferred) {
    const { fortunateOffers: _offers, ...postedTick } = tickOf(command)!; void _offers;
    (command as unknown as EndTurnCommand).tick = postedTick;
    if (deferred.length) fortunateOffersAfterCommit.set(command.operation_id, deferred);
  }
  // Mint a provisional status_id for any tick-added status BEFORE the optimistic apply — the ability pipeline
  // itself never mints one (the commit does, matching ability-truth.ts's identical withIds step for a cast's own
  // verdict.statusesAdded). accepted() swaps these for the server's real ids once the commit answers.
  const tick = tickOf(command);
  // The identical extractor ran on the synthetic outcomes, including every
  // explored random pick. No await or roster mutation occurs between resolution
  // and this read; these input checks cannot introduce a post-draw hand-back.
  if (tick) {
    const prepared = prepareTickInputs(encounter, tick);
    (command as unknown as EndTurnCommand).tick_before_ec = prepared.before;
    (command as unknown as EndTurnCommand).tick = { ...tick, charges: prepared.charges };
  }
  if (tick?.statusesAdded.length) {
    (command as unknown as EndTurnCommand).tick = { ...tickOf(command)!, statusesAdded: tick.statusesAdded.map(s =>
      ({ ...s, status_id: crypto.randomUUID().toUpperCase() })) as unknown as TurnTickResult['statusesAdded'] };
  }
  // The actor's own sound, picked now and played the instant the turn passes (owner 2026-09-10: not tied to a
  // saved roll): the end-turn cue (the route the end_turn / start_turn rows carried) + one expiry cue per ended
  // effect (its own pool, then statusflow/expired). turn_truth_commit_inc.php claims those rows.
  let sound: ReturnType<typeof pickActionSound> = null;
  // Public turns only: the hourglass defaults to a SECRET (2) end turn, whose rows were always silent; a hidden one is
  // GM-only. Those keep their per-viewer server route (secret = no route at all), exactly as before.
  if ((input.rollPrivate ?? 2) === 0) try {
    const w = (scope.command as unknown as { writes?: { ending?: { status_remove?: Row[] }; next?: { status_remove?: Row[] }; others?: Array<{ status_remove?: Row[] }> } }).writes;
    const removals = [...(w?.ending?.status_remove ?? []), ...(w?.next?.status_remove ?? []), ...(w?.others ?? []).flatMap(o => o.status_remove ?? [])];
    const cues: ActionSoundCue[] = [{ slot: 'end', tiers: [['end_turn'], ['die_roll']] }];
    // ONE expiry cue for the boundary: every expiry row routed statusflow/expired → die (the turn lane's rows carry no
    // item id, so an ended effect never replays its own cast take), and one cue covers however many effects end.
    if (removals.length) cues.push({ slot: 'expire', tiers: [['statusflow/expired'], ['die_roll']] });
    sound = pickActionSound(cues, command.operation_id);
    if (sound) (command as unknown as Row).sound = sound;
  } catch (error) {
    console.warn('turn-truth: action sound pick failed (the rows keep their server route)', error);
  }
  let completion: Promise<unknown>;
  bindActionTiming(timing, command.operation_id);
  try {
    if (windowChanged()) return 'refused';
    if (retryBasis !== null) turnRetries.set(command.operation_id, { basis: retryBasis });
    if (deliveryAttempts !== undefined) legendaryAdvanceAttempts.set(command.operation_id, deliveryAttempts);
    stageAppliedEntities(command.operation_id, [tickOf(command)?.spawnRows]);
    completion = outbox.enqueue(withLogParticipants(command)).commit;
    if (sound) void afterActionPaint(() => playActionSound(command.operation_id, sound));
  } catch (error) {
    turnRetries.delete(command.operation_id);
    legendaryAdvanceAttempts.delete(command.operation_id);
    pending.delete(command.operation_id); void repairTurn(command, undefined);
    if (typeof displayWarning === 'function') displayWarning('End Turn could not be committed: ' + (error instanceof Error ? error.message : String(error)));
    window.dispatchEvent(new CustomEvent('ft:turn-truth-refused', { detail: { command, info: { error: String(error), status: 0, terminal: true } } }));
    return 'refused';
  }
  noteOwnOperation(command.operation_id, END_TURN_SCHEMA);
  seen.add(command.operation_id);
  try {
    const result = await settleEndTurnSave(command, completion);
    if (isMovementTruthAcceptedResult(result)) return 'handled';
    if (turnRetries.get(command.operation_id)?.repairing) return await recoverEndTurn(command, result);
    return asRow(asRow(result)?.body)?.legendary_advance_failed === true ? 'failed' : 'refused';
  } finally { legendaryAdvanceAttempts.delete(command.operation_id); turnRetries.delete(command.operation_id); }
}

type TurnTruthWireFrame = ActorTruthCommandBase & { next_id: string; rev: number; next_rev: number; member_revs?: Record<string, number>; frame: EndTurnFrame; tick?: TurnTickResult; committed?: boolean; correction?: boolean; authoritative?: Row; why?: string };
/** Browser turn truth advances each participant under its own confirmed watermark. */
export function handleTurnTruth(command: TurnTruthWireFrame): void {
  if (command?.schema !== END_TURN_SCHEMA || !Number.isInteger(command.rev) || command.rev < 0) return;
  if (command.correction) {
    pending.delete(command.operation_id); void repairTurn(command, command.authoritative); return;
  }
  if (!command.committed || !noteFrameSeen(command.operation_id, 'committed') || seen.has(command.operation_id)) return;
  const row = actorRowFor(command.encounter_id, command.actor_id), frame = asRow(command.frame);
  if (!row || !frame || !asRow(frame.next) || !asRow(frame.encounter)) return;
  const revs = command.member_revs ?? { [command.actor_id]: command.rev, [nextOf(command)]: command.next_rev };
  const allowed = new Set(Object.entries(revs).flatMap(([id, rev]) => {
    const member = actorRowFor(command.encounter_id, id);
    return member && rev >= confirmedRevFor(command.encounter_id, id, member) ? [id] : [];
  }));
  if (!allowed.size) return;
  void playActionSound(command.operation_id, (command as unknown as Row).sound, { peer: true });
  pending.delete(command.operation_id);
  installSaved(command, frame, command.rev, Number(command.next_rev ?? 0), allowed);
  applyTick(command, true, allowed);
  for (const id of allowed) {
    const member = actorRowFor(command.encounter_id, id);
    if (member) bankConfirmedRev(command.encounter_id, id, member, revs[id]!, {});
  }
  if (pendingFor(command.encounter_id, command.actor_id) === 0) reconcileActorRow(command.encounter_id, command.actor_id, row);
  render(command, true, allowed);
}

/** READ-ONLY dry run: the resolver's verdict for the pointer actor without any mutation or request — proves a fixture state
 * is in scope (or names the gate) before a packet spends a cap slot (the S9 lesson). */
function dryRunEndTurn(input: { encounterId: string; actorId: string }): Row {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId);
  const encounter = actorEncounterFor(encounterId), row = actorRowFor(encounterId, actorId);
  if (!entityCompileReady([encounterId, actorId, ...normalizePhpListish((encounter as Row | null)?.encounter_creatures_arr).map(asRow).filter((member): member is Row => member !== null).map(ecId)])) return { reason: 'end-turn:entity-base-unavailable' };
  if (!encounter || !row) return { reason: 'rows-unavailable' };
  const userId = upper(window.FateTide?.user?.id), isGM = window.FateTide?.user?.isGM === true;
  const isStaff = ['employee', 'admin', 'super_admin'].includes(String((window.FateTide?.user as { primaryRole?: string } | undefined)?.primaryRole ?? ''));
  try {
    const snapshot = catalogSnapshot(encounter, { catalogs: 'copy' })();
    const scope = resolveEndTurn({ encounterId, actorId, userId, isGM, isStaff, rollPrivate: 2 }, snapshot, readers(snapshot));
    return scope.supported ? { in_scope: true, next_id: scope.command.next_id, wrap: scope.command.wrap, removals: [...scope.command.writes.ending.status_remove, ...scope.command.writes.next.status_remove].map(r => r.status_id), next_aps: scope.command.writes.next.aps, frame: scope.command.frame }
      : scope.refused ? { refused: true, message: scope.message } : { reason: scope.reason, reasons: scope.reasons ?? [scope.reason] };
  } catch (error) { return { reason: String(error instanceof Error ? error.message : error) }; }
}
Object.assign(window, { ftTryCommitEndTurn: tryCommitEndTurn, ftTurnTruthProbe: { dryRun: dryRunEndTurn },
  ftLegendaryWindowActions: createLegendaryWindowActions({ encounter: id => actorEncounterFor(id) as Row | null,
    pending: pendingFor, advance: advanceLegendaryWindow, refuse: refuseUnported }) });

import { joinAdoptionWarmup } from '../shared/join-adoption-warmup.js';
import { registerActionInputWarmup } from './action-input-warmup.js';
const turnPreloads = new WeakMap<Row, Promise<void>>();
function warmTurnInputs(encounter: Row): void {
  if (turnPreloads.has(encounter)) return;
  const ending = normalizePhpListish(encounter.encounter_creatures_arr).map(asRow).find((r): r is Row => !!r);
  if (!ending) return;
  const job = prefetchTickDefinitions(encounter, ending);
  turnPreloads.set(encounter, job);
  void job.catch(error => { turnPreloads.delete(encounter); console.warn('Turn preload failed', error); });
}
registerActionInputWarmup('turn', encounter => joinAdoptionWarmup(turnPreloads, encounter, () => warmTurnInputs(encounter)));
for (const event of ['ft:encounter-installed', 'ft:encounter-state-received']) window.addEventListener(event, e => {
  const detail = asRow((e as CustomEvent).detail), encounter = asRow(detail?.encounterArr);
  if (encounter && detail?.subType !== 'creature_rows_patched') warmTurnInputs(encounter);
});
const resolvedZoneClaims = new Set<string>();
function zoneClaimKey(encounter: Row, claim: Omit<ZoneEndClaim, 'already_resolved'>): string {
  const clock = campaignFor(encounter);
  return JSON.stringify([encounterField(encounter, 'encounter_id'), clock ? ftClockColsFromArr(clock) : null,
    ...[claim.zone_status_id, claim.caster_id, claim.target_id, claim.item_id].map(upper)]);
}

void Promise.resolve().then(() => {
  for (const encounter of window.FateTide?.encounterCache?.values() ?? []) warmTurnInputs(encounter as Row);
});
